import React, { useEffect, useState } from "react";
import { Modal, Button, Table, Card, Space, DatePicker } from "antd";
import {
  getDayReport,
  getMonthlyReport,
} from "../../apis/nephroPlusService/OHDRService";
import dayjs, { Dayjs } from "dayjs";
import "./index.css";

const { RangePicker } = DatePicker;

interface ReportsModalProps {
  visible: boolean;
  onCancel: () => void;
  hospitalId: number;
  schemeId: number;
}

const ReportsModal: React.FC<ReportsModalProps> = ({
  visible,
  onCancel,
  hospitalId,
  schemeId,
}) => {
  var converter = require("number-to-words");
  const [dayReportData, setDayReportData] = useState<any>(null);
  const [monthlyReportData, setMonthlyReportData] = useState<any>(null);
  const [loadingDayReport, setLoadingDayReport] = useState(false);
  const [loadingMonthlyReport, setLoadingMonthlyReport] = useState(false);

  const [billingDate, setBillingDate] = useState<Dayjs | null>(dayjs());
  const [monthlyRange, setMonthlyRange] = useState<[Dayjs, Dayjs] | null>([
    dayjs().subtract(14, "day"),
    dayjs(),
  ]);

  useEffect(() => {
    if (visible && hospitalId && schemeId) {
      fetchDayReport(billingDate || dayjs());
      fetchMonthlyReport(
        monthlyRange ? monthlyRange[0] : dayjs().subtract(14, "day"),
        monthlyRange ? monthlyRange[1] : dayjs()
      );
    }
    // eslint-disable-next-line
  }, [visible, hospitalId, schemeId, billingDate, monthlyRange]);

  const fetchDayReport = async (date: Dayjs) => {
    setLoadingDayReport(true);
    try {
      const res = await getDayReport({
        date: date.format("YYYY-MM-DD"),
        hospitalId,
        schemeId,
      });
      setDayReportData(res.data);
    } catch (e) {
      setDayReportData(null);
    }
    setLoadingDayReport(false);
  };

  const fetchMonthlyReport = async (start: Dayjs, end: Dayjs) => {
    setLoadingMonthlyReport(true);
    try {
      const res = await getMonthlyReport({
        startDate: start.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
        hospitalId,
        schemeId,
      });
      setMonthlyReportData(res.data);
    } catch (e) {
      setMonthlyReportData(null);
    } finally {
      setLoadingMonthlyReport(false);
    }
  };

  // Extract API data for each panel
  const billingSummary = dayReportData?.data?.billingSummary || {};
  const sessionSummary = dayReportData?.data?.sessionSummary || {};
  const claimSubmissionSummary =
    dayReportData?.data?.claimSubmissionSummary || {};
  const querySummaryDto = dayReportData?.data?.querySummaryDto || {};

  // Prepare horizontal sessionWiseCounts table data
  const sessionWiseCounts = sessionSummary.sessionWiseCounts || {};
  const sessionColumns = Object.keys(sessionWiseCounts).map((key) => ({
    title: `${converter.toWordsOrdinal(key)} Session`,
    dataIndex: key,
    key,
    align: "center" as const,
  }));
  const sessionDataSource = [
    Object.keys(sessionWiseCounts).reduce<{ [key: string]: any }>(
      (row, key) => {
        row[key] =
          sessionWiseCounts[key] == null ? "N/A" : sessionWiseCounts[key];
        return row;
      },
      { key: 1 }
    ),
  ];

  // Prepare monthly report data for the Monthly table
  const monthlyApiData = monthlyReportData?.data || {};
  const monthlyTableData = [
    {
      key: 1,
      label: "PreAuth Count",
      value:
        monthlyApiData.preAuthCount == null
          ? "N/A"
          : monthlyApiData.preAuthCount,
    },
    {
      key: 2,
      label: "Total Sessions",
      value:
        monthlyApiData.totalSessions == null
          ? "N/A"
          : monthlyApiData.totalSessions,
    },
    {
      key: 3,
      label: "Total Sessions Attended",
      value:
        monthlyApiData.totalSessionsAttended == null
          ? "N/A"
          : monthlyApiData.totalSessionsAttended,
    },
    {
      key: 4,
      label: "Last Cycle Completed",
      value:
        monthlyApiData.lastCycleCompleted == null
          ? "N/A"
          : monthlyApiData.lastCycleCompleted,
    },
    {
      key: 5,
      label: "Claim Submitted",
      value:
        monthlyApiData.claimSubmitted == null
          ? "N/A"
          : monthlyApiData.claimSubmitted,
    },
    {
      key: 6,
      label: "More Than One Missed Case",
      value:
        monthlyApiData.moreThanOneMissedCase == null
          ? "N/A"
          : monthlyApiData.moreThanOneMissedCase,
    },
  ];

  return (
    <Modal
      visible={visible}
      title="Reports"
      onCancel={onCancel}
      footer={null}
      width={6000}
      wrapClassName="day-closure-modal"
      bodyStyle={{ maxHeight: "135vh", overflowY: "auto" }}
    >
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
        {/* Daily Report Card */}
        <Card
          title={
            <Space style={{ display: "flex", justifyContent: "space-between" }}>
              <div>Daily Report</div>
              <DatePicker
                allowClear={false}
                value={billingDate}
                onChange={(date) => setBillingDate(date)}
                style={{ marginLeft: 8 }}
                format="DD-MM-YYYY"
                disabledDate={(d) => d > dayjs()}
                size="small"
              />
            </Space>
          }
          // style={{ minWidth: 1050, minHeight: 200, marginBottom: 16, flex: 1 }}
          headStyle={{
            background: "rgb(0, 76, 143)",
            color: "#fff",
            fontWeight: 600,
            fontSize: 18,
          }}
          size="small"
        >
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {/* Billing */}
            <Card
              title={
                <Space>
                  Billing
                  {/* <DatePicker
                    allowClear={false}
                    value={billingDate}
                    onChange={(date) => setBillingDate(date)}
                    style={{ marginLeft: 8 }}
                    format="DD-MM-YYYY"
                    disabledDate={(d) => d > dayjs()}
                    size="small"
                  /> */}
                </Space>
              }
              style={{ width: 200, minHeight: 200, marginBottom: 16 }}
              headStyle={{
                background: "rgb(0, 76, 143)",
                color: "#fff",
                fontWeight: 600,
              }}
              size="small"
            >
              <Table
                columns={[
                  {
                    title: "",
                    dataIndex: "label",
                    key: "label",
                    align: "left",
                    render: (text: string) => (
                      <span
                        style={{
                          // // background: "#fff",
                          color: "#222",
                          display: "block",
                          padding: "8px 12px",
                          borderRadius: "0 4px 4px 0",
                        }}
                      >
                        {text}
                      </span>
                    ),
                  },
                  {
                    title: "",
                    dataIndex: "value",
                    key: "value",
                    align: "right",
                    render: (text: string) => (
                      <span
                        style={{
                          // // background: "#fff",
                          color: "#222",
                          display: "block",
                          padding: "8px 12px",
                          borderRadius: "0 4px 4px 0",
                        }}
                      >
                        {text}
                      </span>
                    ),
                  },
                ]}
                dataSource={[
                  {
                    key: 1,
                    label: "Check In",
                    value:
                      billingSummary.totalCheckedIn == null
                        ? "N/A"
                        : billingSummary.totalCheckedIn,
                  },
                  {
                    key: 2,
                    label: "Bill",
                    value:
                      billingSummary.totalBillsUploaded == null
                        ? "N/A"
                        : billingSummary.totalBillsUploaded,
                  },
                  {
                    key: 3,
                    label: "Checkout",
                    value:
                      billingSummary.totalCheckedOut == null
                        ? "N/A"
                        : billingSummary.totalCheckedOut,
                  },
                ]}
                pagination={false}
                bordered
                className="spaced-table-day-closure"
                size="small"
                showHeader={false}
                loading={loadingDayReport}
                rowClassName={(_, index) =>
                  index % 2 === 0 ? "reportTable-row-alt" : ""
                }
              />
            </Card>

            {/* Sessions */}
            <Card
              title="Sessions"
              style={{ width: 900, minHeight: 200, marginBottom: 16 }}
              headStyle={{
                background: "rgb(0, 76, 143)",
                color: "#fff",
                fontWeight: 600,
              }}
              size="small"
            >
              <h3>Session Wise Count:</h3>
              <Table
                columns={sessionColumns}
                dataSource={sessionDataSource}
                pagination={false}
                bordered
                className="spaced-table-day-closure"
                size="small"
                loading={loadingDayReport}
                style={{ marginBottom: 16 }}
                rowClassName={(_, index) =>
                  index % 2 === 0 ? "reportTable-row-alt" : ""
                }
              />
              <Card
                title={<Space>Missed Sessions</Space>}
                style={{ marginBottom: 16 }}
                headStyle={{
                  background: "rgb(0, 76, 143)",
                  color: "#fff",
                  fontWeight: 600,
                }}
                size="small"
              >
                <Table
                  columns={[
                    {
                      title: "",
                      dataIndex: "label",
                      key: "label",
                      align: "left",
                      render: (text: string) => (
                        <span
                          style={{
                            // background: "#fff",
                            color: "#222",
                            display: "block",
                            padding: "8px 12px",
                            borderRadius: "0 4px 4px 0",
                          }}
                        >
                          {text}
                        </span>
                      ),
                    },
                    {
                      title: "",
                      dataIndex: "value",
                      key: "value",
                      align: "right",
                      render: (text: string) => (
                        <span
                          style={{
                            // background: "#fff",
                            color: "#222",
                            display: "block",
                            padding: "8px 12px",
                            borderRadius: "0 4px 4px 0",
                          }}
                        >
                          {text}
                        </span>
                      ),
                    },
                  ]}
                  dataSource={[
                    {
                      key: 1,
                      label: "Missed 1 session",
                      value:
                        sessionSummary.missedExactlyOneSession == null
                          ? "N/A"
                          : sessionSummary.missedExactlyOneSession,
                    },
                    {
                      key: 2,
                      label: "Missed > 1 session",
                      value:
                        sessionSummary.missedMoreThanOneSession == null
                          ? "N/A"
                          : sessionSummary.missedMoreThanOneSession,
                    },
                  ]}
                  pagination={false}
                  bordered
                  className="spaced-table-day-closure"
                  size="small"
                  showHeader={false}
                  loading={loadingDayReport}
                  style={{ marginBottom: 16 }}
                  rowClassName={(_, index) =>
                    index % 2 === 0 ? "reportTable-row-alt" : ""
                  }
                />
              </Card>
              <Table
                columns={[
                  {
                    title: "Morning",
                    dataIndex: "morningSessions",
                    key: "morningSessions",
                    align: "center" as const,
                  },
                  {
                    title: "Afternoon",
                    dataIndex: "afternoonSessions",
                    key: "afternoonSessions",
                    align: "center" as const,
                  },
                  {
                    title: "Evening",
                    dataIndex: "eveningSessions",
                    key: "eveningSessions",
                    align: "center" as const,
                  },
                ]}
                dataSource={[
                  {
                    key: 1,
                    morningSessions:
                      sessionSummary.morningSessions == null
                        ? "N/A"
                        : sessionSummary.morningSessions,
                    afternoonSessions:
                      sessionSummary.afternoonSessions == null
                        ? "N/A"
                        : sessionSummary.afternoonSessions,
                    eveningSessions:
                      sessionSummary.eveningSessions == null
                        ? "N/A"
                        : sessionSummary.eveningSessions,
                  },
                ]}
                pagination={false}
                bordered
                className="spaced-table-day-closure"
                size="small"
                loading={loadingDayReport}
                rowClassName={(_, index) =>
                  index % 2 === 0 ? "reportTable-row-alt" : ""
                }
              />
            </Card>

            {/* Claim Submission */}
            <Card
              title="Claim Submission"
              style={{ width: 320, minHeight: 200, marginBottom: 16 }}
              headStyle={{
                background: "rgb(0, 76, 143)",
                color: "#fff",
                fontWeight: 600,
              }}
              size="small"
            >
              <Table
                columns={[
                  {
                    title: "",
                    dataIndex: "label",
                    key: "label",
                    align: "left",
                    render: (text: string) => (
                      <span
                        style={{
                          // background: "#fff",
                          color: "#222",
                          display: "block",
                          padding: "8px 12px",
                          borderRadius: "0 4px 4px 0",
                        }}
                      >
                        {text}
                      </span>
                    ),
                  },
                  {
                    title: "",
                    dataIndex: "value",
                    key: "value",
                    align: "right",
                    render: (text: string) => (
                      <span
                        style={{
                          // background: "#fff",
                          color: "#222",
                          display: "block",
                          padding: "8px 12px",
                          borderRadius: "0 4px 4px 0",
                        }}
                      >
                        {text}
                      </span>
                    ),
                  },
                ]}
                dataSource={[
                  {
                    key: 1,
                    label: "Last Session",
                    value:
                      claimSubmissionSummary.lastSessionCount == null
                        ? "N/A"
                        : claimSubmissionSummary.lastSessionCount,
                  },
                  {
                    key: 2,
                    label: "More Than One Missed",
                    value:
                      claimSubmissionSummary.missedMoreThanOneSession == null
                        ? "N/A"
                        : claimSubmissionSummary.missedMoreThanOneSession,
                  },
                  {
                    key: 3,
                    label: "Claims Submitted",
                    value:
                      claimSubmissionSummary.claimsSubmitted == null
                        ? "N/A"
                        : claimSubmissionSummary.claimsSubmitted,
                  },
                  {
                    key: 4,
                    label: "Total",
                    value:
                      claimSubmissionSummary.totalCases == null
                        ? "N/A"
                        : claimSubmissionSummary.totalCases,
                  },
                  {
                    key: 5,
                    label: "Pending",
                    value:
                      claimSubmissionSummary.claimsPending == null
                        ? "N/A"
                        : claimSubmissionSummary.claimsPending,
                  },
                ]}
                pagination={false}
                bordered
                className="spaced-table-day-closure"
                size="small"
                showHeader={false}
                loading={loadingDayReport}
                rowClassName={(_, index) =>
                  index % 2 === 0 ? "reportTable-row-alt" : ""
                }
              />
            </Card>

            {/* Query */}
            <Card
              title="Query"
              style={{ width: 250, minHeight: 200, marginBottom: 16 }}
              headStyle={{
                background: "rgb(0, 76, 143)",
                color: "#fff",
                fontWeight: 600,
              }}
              size="small"
            >
              <Table
                columns={[
                  {
                    title: "",
                    dataIndex: "label",
                    key: "label",
                    align: "left",
                    render: (text: string) => (
                      <span
                        style={{
                          // background: "#fff",
                          color: "#222",
                          display: "block",
                          padding: "8px 12px",
                          borderRadius: "0 4px 4px 0",
                        }}
                      >
                        {text}
                      </span>
                    ),
                  },
                  {
                    title: "",
                    dataIndex: "value",
                    key: "value",
                    align: "right",
                    render: (text: string) => (
                      <span
                        style={{
                          // background: "#fff",
                          color: "#222",
                          display: "block",
                          padding: "8px 12px",
                          borderRadius: "0 4px 4px 0",
                        }}
                      >
                        {text}
                      </span>
                    ),
                  },
                ]}
                dataSource={[
                  {
                    key: 1,
                    label: "Internal",
                    value:
                      querySummaryDto.internal == null
                        ? "N/A"
                        : querySummaryDto.internal,
                  },
                  {
                    key: 2,
                    label: "Payer",
                    value:
                      querySummaryDto.payer == null
                        ? "N/A"
                        : querySummaryDto.payer,
                  },
                ]}
                pagination={false}
                bordered
                className="spaced-table-day-closure"
                size="small"
                showHeader={false}
                loading={loadingDayReport}
                rowClassName={(_, index) =>
                  index % 2 === 0 ? "reportTable-row-alt" : ""
                }
              />
            </Card>
          </div>
        </Card>

        {/* Monthly Report Card */}
        <Card
          title={
            <Space style={{ display: "flex", justifyContent: "space-between" }}>
              <div>Monthly Report</div>
              <RangePicker
                allowClear={false}
                value={monthlyRange}
                onChange={(range) => setMonthlyRange(range as [Dayjs, Dayjs])}
                style={{ marginLeft: 8 }}
                format="DD-MM-YYYY"
                disabledDate={(d) => d > dayjs()}
                size="small"
              />
            </Space>
          }
          style={{ minWidth: 500, minHeight: 200, marginBottom: 16, flex: 1 }}
          headStyle={{
            background: "rgb(0, 76, 143)",
            color: "#fff",
            fontWeight: 600,
            fontSize: 18,
          }}
          size="small"
        >
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Card
              title={<Space>Summary</Space>}
              style={{ width: 1200, minHeight: 200, marginBottom: 16 }}
              headStyle={{
                background: "rgb(0, 76, 143)",
                color: "#fff",
                fontWeight: 600,
              }}
              size="small"
            >
              <Table
                columns={[
                  {
                    title: "",
                    dataIndex: "label",
                    key: "label",
                    align: "left",
                    render: (text: string) => (
                      <span
                        style={{
                          // background: "#fff",
                          color: "#222",
                          display: "block",
                          padding: "8px 12px",
                          borderRadius: "0 4px 4px 0",
                        }}
                      >
                        {text}
                      </span>
                    ),
                  },
                  {
                    title: "",
                    dataIndex: "value",
                    key: "value",
                    align: "right",
                    render: (text: string) => (
                      <span
                        style={{
                          // background: "#fff",
                          color: "#222",
                          display: "block",
                          padding: "8px 12px",
                          borderRadius: "0 4px 4px 0",
                        }}
                      >
                        {text}
                      </span>
                    ),
                  },
                ]}
                dataSource={monthlyTableData}
                pagination={false}
                bordered
                className="spaced-table-day-closure"
                size="small"
                showHeader={false}
                loading={loadingMonthlyReport}
                rowClassName={(_, index) =>
                  index % 2 === 0 ? "reportTable-row-alt" : ""
                }
              />
            </Card>
          </div>
        </Card>
      </div>
    </Modal>
  );
};

export default ReportsModal;
